import React from "react";
import { Icon } from "@abb/abb-common-ux-react";
import NoTrendToDisplay from "../shared/NoTrendToDisplay";

import {
  ChartTypes,
  DateUnits,
  ISeries,
  ITrendDataLimit,
  Trend,
  TrendMarkerTypes,
} from "@iom/trend";
import _ from "lodash";
import { MeasurementSetting } from "../../models/settings/MeasurementSettings";
import InfiniteScroll from "react-infinite-scroll-component";
import IThresholdSettings from "../../models/settings/ThresholdSettings";
import * as CONSTANTS from "../../utilities/Constants";
import utility from "../shared/Utility";
import { Translation } from "react-i18next";
import measurementSettingStore from "../../flux/stores/settings/MeasurementSettingStore";

interface ILandingprops {
  selectedCalculatedDetails: any;
  thresholdSettings: any;
  showCalculatedPropertyDetails: (data: any) => void;
}

interface IState {
  toggleGraph: boolean;
  series: any;
  chartType: ChartTypes;
  dateUnit: DateUnits;
  dateUnitCount: number;
  start?: Date;
  end?: Date;
}

export default class CalculatedPropertyDetails extends React.Component<
  ILandingprops,
  IState
> {
  start = new Date(2019, 0, 1, 0, 0, 0, 0);
  end = new Date(2019, 0, 5, 0, 0, 0, 0);
  private minXValue: number;
  private maxXValue: number;
  private _autolineScroll: any;
  private _mount: boolean;
  private _hasData: boolean = true;
  private _page: any = 1;

  constructor(props: any) {
    super(props);
    let xValue: number[] = [];

    this.minXValue = 0;
    this.maxXValue = 200;
    this.state = {
      toggleGraph: false,
      chartType: "Bar",
      dateUnit: "Day",
      dateUnitCount: 4,
      start: this.start,
      end: this.end,
      series: [
        {
          name: "Position",
          color: "#5ABFAE",
          id: "1",
          markerStyle: {
            type: this.state?.chartType ==="Bar" ? "None" : "Circle",
          },
          yAxis: {
            min: this.minXValue,
            max: this.maxXValue,
            decimalPlaces: 0,
          },
          isShownInChart: true,
          points: this.generateOrdinalData1(0, 20, 1, 0, 100), // String
        },
      ],
    };
  }
  componentDidMount(): void {
    this.setState({
      chartType: measurementSettingStore.getChartType(),
    });
  }

  generateOrdinalData1(
    fromDate: number,
    toDate: number,
    interval = 1,
    min: number,
    max: number,
    isNumber = false
  ) {
    const data = [
      {
        xValue: "Position",
        value: 50,
      },
    ];
    return data;
  }

  onChartTypeChange = (chartType: ChartTypes) => {
    this.setState({
      chartType: chartType,
    });
    return chartType;
  };

  onSeriesChartTypeChange = (value: string, series: ISeries) => {
    let updatedValue: ISeries[] = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        return { ...item, chartType: value } as ISeries;
      } else {
        return item;
      }
    });
    this.setState({ series: updatedValue });
  };

  onSeriesColorChange = (value: string, series: ISeries) => {
    let updatedValue: ISeries[] = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        return { ...item, color: value } as ISeries;
      } else {
        return item;
      }
    });
    this.setState({ series: updatedValue });
  };

  getMarkerStyles = (value: string, item: ISeries) => {
    let marker = item.markerStyle ? { ...item.markerStyle } : {};
    if (value === "Circle") {
      marker.type = "Circle";
      marker.fillColor = "white";
    } else if (value === "Square") {
      marker.type = "Square";
      marker.fillColor = "white";
    } else if (value === "FillCircle") {
      marker.type = "Circle";
      marker.fillColor = "SeriesColor";
    } else if (value === "FillSquare") {
      marker.type = "Square";
      marker.fillColor = "SeriesColor";
    } else {
      marker.type = "None";
    }
    return marker;
  };

  onSeriesMarkerChange = (value: TrendMarkerTypes, series: ISeries) => {
    let updatedValue: ISeries[] = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        return {
          ...item,
          markerStyle: { ...series.markerStyle, type: value },
        } as ISeries;
      } else {
        return item;
      }
    });
    this.setState({ series: updatedValue });
  };

  onSeriesFillTypeChange = (value: string, series: ISeries) => {
    let updatedValue: ISeries[] = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        return { ...item, fillType: value } as ISeries;
      } else {
        return item;
      }
    });
    this.setState({ series: updatedValue });
  };

  onInvalidPointTypeChange = (value: string, series: ISeries) => {
    let updatedValue: ISeries[] = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        return { ...item, invalidPointLineType: value } as ISeries;
      } else {
        return item;
      }
    });
    this.setState({ series: updatedValue });
  };

  onDateRangeChange = (
    start: string | number | Date,
    end: string | number | Date,
    dateUnit: DateUnits,
    dateUnitCount: number,
    isOnZoom: boolean
  ) => {
    this.setState({
      dateUnit,
      dateUnitCount,
      start: start as Date,
      end: end as Date,
    });
  };

  onDataLimitVisibilityChange = (
    series: ISeries,
    limit: ITrendDataLimit,
    isShown: boolean
  ) => {
    let updatedValue = this.state.series.map((item: any) => {
      if (item.name === series.name) {
        let limits = item.dataLimits.map((line: ITrendDataLimit) => {
          if (line.name === limit.name) {
            return { ...line, shownInChart: isShown };
          } else {
            return line;
          }
        });
        return { ...item, dataLimits: limits };
      } else {
        return item;
      }
    });

    this.setState({ series: updatedValue });
  };

  /// Get right color to show in dashboard screen.
  /// Dashboard measurement statistics will not have direct value status, so need to pick right color by looking in all available positions
  /// default color is black
  private getRightColorForDashboardView(
    allValueRangesForAPosition: any[]
  ): string {
    let color = CONSTANTS.WITHIN_LIMIT_COLOR;
    if (
      allValueRangesForAPosition?.filter(
        (x) => x?.valueRange?.name === CONSTANTS.ValueRange.AlarmLimit1
      )?.length > 0
    ) {
      color = this.props.thresholdSettings.find(
        (x: any) => x.name === "Alarm Limit1"
      )?.colorCode;
    } else if (
      allValueRangesForAPosition?.filter(
        (x) => x?.valueRange?.name === CONSTANTS.ValueRange.AlarmLimit2
      )?.length > 0
    ) {
      color = this.props.thresholdSettings.find(
        (x: any) => x.name === "Alarm Limit2"
      )?.colorCode;
    } else if (
      allValueRangesForAPosition?.filter(
        (x) => x?.valueRange?.name === CONSTANTS.ValueRange.RejectLimit
      )?.length > 0
    ) {
      color = this.props.thresholdSettings.find(
        (x: any) => x.name === "Reject Limit"
      )?.colorCode;
    }
    return color;
  }
  // MEASURED END

  // CALCULATED DSTART
  private createCalculatedHeader() {
    const items: any[] = [];
    for (
      let index = 0;
      index < this.props.selectedCalculatedDetails.calculatedValues.length;
      index++
    ) {
      items.push(
        <th scope="col" key={index}>
          <span></span>
        </th>
      );
    }
    return items;
  }

  private createCalculatedColumnData() {
    const items: any[] = [];
    const trendData: any = [];

    if (this.props.selectedCalculatedDetails.calculatedValues?.length != 0) {
      this.props.selectedCalculatedDetails.calculatedValues.forEach(
        (item: any) => {
          if (item.count == 0) {
            trendData.push({
              xValue: "Calculated",
              value: item.value,
            });
          } else {
            trendData.push({
              xValue: item.positionName
                ? item.positionName
                : item.count.toString(),
              value: item.value,
            });
          }
        }
      );
      if (trendData.length != 0) {
        
          trendData.push({ xValue: "", value: null });
      }
      const propertyDetails: any = this.props.selectedCalculatedDetails;

      const yValue: number[] = [
        propertyDetails.alarmLow1,
        propertyDetails.alarmLow2,
        propertyDetails.alarmHigh1,
        propertyDetails.alarmHigh2,
        propertyDetails.rejectLow,
        propertyDetails.rejectHigh,
      ];
      trendData.forEach((item: any) => {
        yValue.push(item.value);
      });

      let minYValue: any = 0;
      let maxYValue: any = 0;

      if (Math.sign(_.min(yValue)) === -1) {
        minYValue = _.min(yValue) + (_.min(yValue) / 100) * 5;
        maxYValue = _.max(yValue) - (_.max(yValue) / 100) * 5;
      } else {
        // ITC Changes
        minYValue =
          _.min(_.without(yValue, 0)) - (_.min(_.without(yValue, 0)) / 100) * 5;
        // const minYValue: any = 0;
        maxYValue = _.max(yValue) + (_.max(yValue) / 100) * 5;
      }

      const series1: any = [
        {
          name: "Mean",
          color: "#5ABFAE",
          id: "1",
          markerStyle: {
            type: this.state?.chartType ==="Bar" ? "None" : "Circle",
          },
          yAxis: {
            min: minYValue,
            max: maxYValue,
            decimalPlaces: this.props.selectedCalculatedDetails?.decimals,
          },
          dataLimits: [
            {
              name: "Alarm Low Limit-1",
              shownInChart: true,
              value: propertyDetails.alarmLow1,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Alarm Low 1" || x.name === "Alarm Limit1"
                ).colorCode,
              },
            },
            {
              name: "Alarm High Limit-1",
              shownInChart: true,
              value: propertyDetails.alarmHigh1,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Alarm High 1" || x.name === "Alarm Limit1"
                ).colorCode,
              },
            },
            {
              name: "Alarm Low Limit-2",
              shownInChart: true,
              value: propertyDetails.alarmLow2,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Alarm Low 2" || x.name === "Alarm Limit2"
                ).colorCode,
              },
            },
            {
              name: "Alarm High Limit-2",
              shownInChart: true,
              value: propertyDetails.alarmHigh2,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Alarm High 2" || x.name === "Alarm Limit2"
                ).colorCode,
              },
            },
            {
              name: "Reject Low",
              shownInChart: true,
              value: propertyDetails.rejectLow,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Reject Low" || x.name === "Reject Limit"
                ).colorCode,
              },
            },
            {
              name: "Reject High",
              shownInChart: true,
              value: propertyDetails.rejectHigh,
              className: "custom-line-graph",
              style: {
                stroke: this.props.thresholdSettings?.find(
                  (x: any) =>
                    x.name === "Reject High" || x.name === "Reject Limit"
                ).colorCode,
              },
            },
          ],
          isShownInChart: true,
          points: trendData,
        },
      ];

      items.push(
        <tr
          style={{ height: "150px" }}
          className="normal-class"
          key={Math.random()}
        >
          <td className="instrument">
            <div>
              {this.props.selectedCalculatedDetails.name}
              <span>
                {this.props.selectedCalculatedDetails.unit}
                <span className="val">
                  (
                  {(
                    this.props.selectedCalculatedDetails.calculatedValues.reduce(
                      (total: any, next: any) => total + next.value,
                      0
                    ) /
                    this.props.selectedCalculatedDetails.calculatedValues.length
                  ).toFixed(this.props.selectedCalculatedDetails.decimals)}
                  )
                </span>
              </span>
            </div>
          </td>
          <td className="trend-table">
            {series1[0].points?.length > 0 ? (
              <Trend
                chartType={this.state.chartType}
                invalidPointLineType="None"
                invalidStatuses={["Invalid"]}
                dateUnit={this.state.dateUnit}
                dateUnitCount={this.state.dateUnitCount}
                gridLineColor="grey"
                gridLineWidth={0.5}
                showVerticalGridLines={false}
                showHorizontalGridLines={true}
                series={series1}
                xAxis={{ type: "Ordinal", tickAngle: 45, height: 15 }}
                onChartTypeChange={this.onChartTypeChange}
                onDataLimitVisibilityChange={this.onDataLimitVisibilityChange}
                onDateRangeChange={this.onDateRangeChange}
                onInvalidPointTypeChange={this.onInvalidPointTypeChange}
                onSeriesChartTypeChange={this.onSeriesChartTypeChange}
                onSeriesColorChange={this.onSeriesColorChange}
                onSeriesFillTypeChange={this.onSeriesFillTypeChange}
                onSeriesMarkerChange={this.onSeriesMarkerChange}
                trendChartHeight={150}
                showChartType={false}
                showDateTimePicker={false}
                showOuterBorder={false}
                showTableData={false}
                yAxisTicksCount={2}
                yAxisWidth={75}
              />
            ) : (
              <div>
                <NoTrendToDisplay></NoTrendToDisplay>
              </div>
            )}
          </td>
        </tr>
      );
      return items;
    } else {
      return null;
    }
  }

  toggleGraphView = () => {
    this.setState({
      toggleGraph: !this.state.toggleGraph,
    });
  };
  render() {
    return (
      <Translation>
        {(t) => (
          <React.Fragment>
            <div className="col-12">
              <button
                title="back"
                className="icon-button circle active"
                onClick={(e) => this.props.showCalculatedPropertyDetails("")}
              >
                <Icon name="abb/left" sizeClass="small" />
              </button>
              <ul className="navbar-nav propertyFilterList">
                {this.state.toggleGraph ? (
                  <button
                    title="title"
                    className={
                      this.state.toggleGraph
                        ? "icon-button circle active"
                        : "icon-button circle"
                    }
                    onClick={this.toggleGraphView}
                  >
                    <Icon name="abb/list" sizeClass="small" />
                  </button>
                ) : (
                  <button
                    title="title"
                    className={
                      this.state.toggleGraph
                        ? "icon-button circle active"
                        : "icon-button circle"
                    }
                    onClick={this.toggleGraphView}
                  >
                    <Icon name="abb/trend-3" sizeClass="small" />
                  </button>
                )}
              </ul>
              <div className="divider"></div>
              <div className="table-data-container">
                <table className="table propertyTable editProperty">
                  <thead>
                    <tr>
                      <th className="instrument">
                        <div className="measureItems  cursor-default">
                          <div className="details">
                            <p className="title">
                              {this.props.selectedCalculatedDetails.name}
                            </p>
                            <p></p>
                            <p></p>
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  {!this.state.toggleGraph ? (
                    <tbody className="background-dark-gray">
                      <tr className="">
                        <td className="instrument">
                          <div>
                            <span>
                              <span className="val"></span>
                            </span>
                          </div>
                        </td>
                        {this.props.selectedCalculatedDetails.calculatedValues.map(
                          (item: any, index: number) => {
                            return <td className="property">{index + 1}</td>;
                          }
                        )}
                      </tr>
                      <tr
                        className="background-dark-gray"
                        style={{ background: "#e9e9e9 !important" }}
                      >
                        <td className="instrument">
                          <div>
                            {this.props.selectedCalculatedDetails.name}
                            <span>
                              {this.props.selectedCalculatedDetails.unit}
                              <span className="val">
                                (
                                {(
                                  this.props.selectedCalculatedDetails.calculatedValues.reduce(
                                    (total: any, next: any) =>
                                      total + next.value,
                                    0
                                  ) /
                                  this.props.selectedCalculatedDetails
                                    .calculatedValues.length
                                ).toFixed(
                                  this.props.selectedCalculatedDetails.decimals
                                )}
                                )
                              </span>
                            </span>
                          </div>
                        </td>
                        {this.props.selectedCalculatedDetails.calculatedValues.map(
                          (item: any) => {
                            return (
                              <td className="property">
                                {item.value.toFixed(
                                  this.props.selectedCalculatedDetails.decimals
                                )}
                              </td>
                            );
                          }
                        )}
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>{this.createCalculatedColumnData()}</tbody>
                  )}
                </table>
              </div>
            </div>
          </React.Fragment>
        )}
      </Translation>
    );
  }
}
